/**
 * Every SubKrewe and every member, live, as the covering sources
 * (utils/liveData.ts): whoever holds them (fetchSubKrewes, fetchMembers)
 * shares one listener on each collection, and single records or queries in
 * them are answered from these while they run.
 *
 * Those who read every member record (admins, band admins, red shirt admins)
 * hold them from sign-in, with the rest of the admin live set (start), so
 * the admin pages, search and the command palette never wait on the network.
 *
 * The admin SubKrewes page used to load each SubKrewe's members with a query
 * per SubKrewe (176 of them) and a store update per query, which took 25
 * seconds on the prod copy. Here each snapshot is one store update, and who
 * is in which SubKrewe is worked out in memory (utils/adminRosters.ts): on
 * the first load for every SubKrewe, after that only for the SubKrewes a
 * change touches.
 */
import type { DocumentReference } from 'firebase/firestore'
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore'
import { defineStore } from 'pinia'
import { useFirestore } from 'vuefire'
import type { Member, MemberFirestoreData } from '~/shared/types/member.js'
import type { SubKrewe, SubKreweFirestoreData } from '~/shared/types/subkrewe'
import { membersBySubkrewe, subkrewesTouchedBy } from '~/utils/adminRosters'
import type { LiveChange, LiveSpec } from '~/utils/liveData'
import { acquireLive, isLiveRunning, liveQueryListener, whenLiveSettled } from '~/utils/liveData'

// The references behind the objects, kept for the grouping: member id →
// SubKrewe id, SubKrewe id → captain ids
const memberSubkrewe = new Map<string, string | null>()
const captainIds = new Map<string, string[]>()

const refId = (ref: unknown): string | null =>
  ref && typeof ref === 'object' && typeof (ref as DocumentReference).id === 'string' ? (ref as DocumentReference).id : null

const idOf = (path: string) => path.slice(path.indexOf('/') + 1)

/** The session group the admin live set is held under */
export const ADMIN_LIVE_SESSION = 'admin'

// start() has held the set (until sign-out stops the sources)
let started = false
// start() holds every member once the SubKrewes are in
let membersWaiting = false

// Once the page has rendered what came in, and started the listeners that
// needs (the SubKrewe browser's stats)
const whenIdle = () => new Promise<void>((resolve) => {
  if (typeof requestIdleCallback === 'function') requestIdleCallback(() => resolve(), { timeout: 2000 })
  else setTimeout(resolve, 0)
})

// Not forever: a source that hangs mustn't keep every member away
const MEMBERS_WAIT_MAX_MS = 5000
const whenPageLoaded = () => Promise.race([
  whenLiveSettled(),
  new Promise<void>(resolve => setTimeout(resolve, MEMBERS_WAIT_MAX_MS)),
])

export const useAdminLiveStore = defineStore('adminLive', {
  state: () => ({
    haveSubkrewes: false,
    haveMembers: false,
  }),

  getters: {
    /** Both first snapshots are in and the rosters are built */
    ready(): boolean {
      return this.haveSubkrewes && this.haveMembers
    },
  },

  actions: {
    /**
     * Hold the admin live set for the session: every SubKrewe and member,
     * system config, products, inventory, this year's lineup. The admin
     * task queue is held by the core subscriptions. Safe to call again.
     *
     * Every member comes last, once the SubKrewes are in and the page's own
     * listeners are ready. Firestore runs every listener over one stream and
     * raises their snapshots in sync, so while thousands of member records
     * load, no other listener's first snapshot comes through, the page's own
     * included (2-5 s on a reload). Until then SubKrewes resolve their captains as everyone
     * else's do, and a page that needs every member (fetchMembers) starts the
     * listener itself.
     */
    start() {
      if (started && isLiveRunning('subkrewes') && (membersWaiting || isLiveRunning('members'))) return
      started = true
      const hold = { session: ADMIN_LIVE_SESSION }
      const fail = (what: string) => (error: unknown) => console.error(`[AdminLive] ${what} failed:`, error)
      membersWaiting = true
      // Alongside the SubKrewes, which resolve their captains till then
      useMembersStore().fetchCaptains().catch(fail('Captains'))
      acquireLive(this.subkrewesSpec(), hold)
        .catch(fail('SubKrewes'))
        .then(whenIdle)
        .then(whenPageLoaded)
        .then(() => {
          membersWaiting = false
          return acquireLive(this.membersSpec(), hold)
        })
        .catch(fail('Members'))
      acquireLive(useProductsStore().allProductsSpec(), hold).catch(fail('Products'))
      acquireLive(useInventoryStore().allItemsSpec(), hold).catch(fail('Inventory'))
      const systemConfig = useSystemConfigStore()
      systemConfig.loadConfig().then(() => {
        acquireLive(useLineupStore().lineupSpec(systemConfig.paradeYear), hold).catch(fail('Lineup'))
      }).catch(fail('System config'))
    },

    subkrewesSpec(): LiveSpec<SubKreweFirestoreData> {
      return {
        key: 'subkrewes',
        covers: 'subkrewes',
        listen: (emit, fail) => onSnapshot(
          query(collection(useFirestore(), 'subkrewes'), orderBy('name')),
          ...liveQueryListener('subkrewes', emit, fail),
        ),
        apply: changes => this.applySubkrewes(changes),
        left: (paths) => {
          for (const path of paths) captainIds.delete(idOf(path))
        },
        evict: paths => useSubKrewesStore().evictSubkrewes(paths),
        ready: () => {
          this.haveSubkrewes = true
          useSubKrewesStore().$patch({ initialized: true, availableLoaded: true })
          this.fillRosters(null)
        },
        stopped: () => {
          captainIds.clear()
          this.haveSubkrewes = false
        },
      }
    },

    membersSpec(): LiveSpec<MemberFirestoreData> {
      return {
        key: 'members',
        covers: 'members',
        listen: (emit, fail) => onSnapshot(
          collection(useFirestore(), 'members'),
          ...liveQueryListener('members', emit, fail),
        ),
        apply: changes => this.applyMembers(changes),
        left: (paths) => {
          const touched = new Set<string>()
          for (const path of paths) {
            const id = idOf(path)
            const before = memberSubkrewe.get(id)
            memberSubkrewe.delete(id)
            subkrewesTouchedBy(id, before, null, captainIds).forEach(sk => touched.add(sk))
          }
          // After evict: the member record is gone from the store by then
          queueMicrotask(() => this.fillRosters(touched))
        },
        evict: paths => useMembersStore().evictMembers(paths),
        ready: () => {
          this.haveMembers = true
          this.fillRosters(null)
        },
        stopped: () => {
          memberSubkrewe.clear()
          this.haveMembers = false
          useSubKrewesStore().subkreweMembersLoaded = false
        },
      }
    },

    /**
     * SubKrewes from the covering listener. Without every member live (a
     * non-admin who holds every SubKrewe, or an admin before the members
     * are in), the captains resolve the usual way (subkrewes.applySubkrewes);
     * with them, from the member records.
     */
    async applySubkrewes(changes: LiveChange<SubKreweFirestoreData>[]) {
      const subkrewesStore = useSubKrewesStore()
      for (const { path, data } of changes) {
        const captains = Array.isArray(data!.captains) ? data!.captains : []
        // Once each, as convertToSubKrewe does: older signup claims stored
        // some captains twice
        captainIds.set(idOf(path), [...new Set(captains.map(refId).filter((captain): captain is string => Boolean(captain)))])
      }
      if (!this.haveMembers) {
        await subkrewesStore.applySubkrewes(changes)
        return
      }
      const updates: Record<string, SubKrewe> = {}
      for (const { path, data } of changes) {
        // Captains and members are filled in below, from the member records
        updates[path] = subkrewesStore.convertToSubKrewe(idOf(path), data!, false)
      }
      subkrewesStore.$patch((state) => {
        Object.assign(state.subkrewes, updates)
      })
      this.fillRosters(new Set(changes.map(({ path }) => idOf(path))))
    },

    applyMembers(changes: LiveChange<MemberFirestoreData>[]) {
      const membersStore = useMembersStore()
      const subkrewesStore = useSubKrewesStore()
      // Before both are in, ready() fills every roster
      const tracking = this.ready
      const touched = new Set<string>()
      const updates: Record<string, Member> = {}
      for (const { path, data } of changes) {
        const id = idOf(path)
        const before = memberSubkrewe.get(id)
        const subkreweId = refId(data!.subkrewe)
        memberSubkrewe.set(id, subkreweId)
        const member = membersStore.convertToMember(id, data!, false)
        member.subkrewe = subkreweId ? subkrewesStore.subkrewes[`subkrewes/${subkreweId}`] ?? null : null
        // The signed-in member's own listener reacts to a type change by
        // comparing with this (members.applyMembers)
        const lastSyncedType = membersStore.members[path]?._lastSyncedType
        if (lastSyncedType) member._lastSyncedType = lastSyncedType
        updates[path] = member
        if (tracking) subkrewesTouchedBy(id, before, subkreweId, captainIds).forEach(sk => touched.add(sk))
      }
      membersStore.$patch((state) => {
        Object.assign(state.members, updates)
      })
      this.fillRosters(touched)
    },

    /**
     * Put members and captains on the SubKrewes (all of them, or just
     * `only`), and point each of their members at the SubKrewe object
     */
    fillRosters(only: Set<string> | null) {
      if (!this.ready) return
      if (only && only.size === 0) return
      const membersStore = useMembersStore()
      const subkrewesStore = useSubKrewesStore()
      const ids = only ?? new Set(Object.values(subkrewesStore.subkrewes).map(s => s.id))
      const rosters = membersBySubkrewe(memberSubkrewe, ids)
      const member = (id: string) => membersStore.members[`members/${id}`]

      subkrewesStore.$patch((state) => {
        for (const id of ids) {
          const subkrewe = state.subkrewes[`subkrewes/${id}`]
          if (!subkrewe) continue
          subkrewe.members = (rosters.get(id) ?? []).map(member).filter((m): m is Member => Boolean(m))
          subkrewe.captains = (captainIds.get(id) ?? []).map(member).filter((m): m is Member => Boolean(m))
        }
        state.subkreweMembersLoaded = true
      })
      membersStore.$patch((state) => {
        for (const id of ids) {
          const subkrewe = subkrewesStore.subkrewes[`subkrewes/${id}`] ?? null
          for (const memberId of rosters.get(id) ?? []) {
            const stored = state.members[`members/${memberId}`]
            if (stored) stored.subkrewe = subkrewe
          }
        }
      })
    },
  },
})
